import { BlogDetailedHeader } from '@/components/blog-detailed/blog-detailed-header';
import { CMSColumns } from '@/components/cms/cms-columns';
import { useQuery } from '@tanstack/react-query';
import { CMSBanner } from '@/components/cms/cms-banner';
import { CMSTabs } from '@/components/cms/cms-tabs';
import { CMSTextAdvanced } from '@/components/cms/cms-text-advanced';
import { CMSCard } from '@/components/cms/cms-card';
import { CMSPlanCardsWrapper } from '@/components/cms/cms-plan-cards-wrapper';
import { CMSCTAButton } from '@/components/cms/cms-cta-button';
import { cn } from '@/lib/utils';
import { CMSText } from '@/components/cms/cms-text';
import { cmsQueryKeys } from '@/services/cms/cmsQueryKeys';
import { getBlogDetailedContent } from '@/services/cms/getBlogDetailedContent';
import { useParams } from '@tanstack/react-router';
import { match } from 'ts-pattern';
import './index.css';
import { RelatedArticles } from '../../components/blog-detailed/related-articles';
import { CMSImage } from '@/components/cms/cms-image';
import { FullScreenLoadingSpinner } from '@/components/full-screen-loading-spinner';
import { Suspense, useEffect } from 'react';
import { CMSPlaceholder } from '@/components/cms/cms-placeholder';
import { CMSSeo } from '@/components/cms-seo';
import { BLOG_SEO } from '@/constants/seo.constants';
import { ErrorBoundaryFallback } from '@/components/fallback';
import { ErrorBoundary } from 'react-error-boundary';
import { BlogNotFound } from '@/services/cms/getBlogDetailedContent/BlogNotFound';
import PageNotFound from '@/components/page-not-found';
import React from 'react';

const LazyCMSVideo = React.lazy(() => import('@/components/cms/cms-video'));

const BlogDetailedPage = () => {
  const { slug } = useParams({ from: '/_layout/blog/$slug' });

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [slug]);

  const { isPending, data, isError, error } = useQuery({
    queryFn: getBlogDetailedContent,
    queryKey: [cmsQueryKeys.getBlogDetailedContent[0], slug],
    enabled: true,
    staleTime: Infinity,
  });

  if (isPending) {
    return (
      <div className="min-h-[95vh] flex items-center justify-center">
        <FullScreenLoadingSpinner />;
      </div>
    );
  }

  if (isError) {
    if (error instanceof BlogNotFound) {
      return <PageNotFound className="mb-[100px] md:mb-[161px]" />;
    }
    return <h1>Error</h1>;
  }

  return (
    <>
      <CMSSeo
        data={{
          seo_page_title: data.seo_page_title ?? BLOG_SEO.title,
          seo_description: data.seo_description ?? BLOG_SEO.description,
          seo_keywords: data.seo_keywords ?? BLOG_SEO.seo_keywords,
        }}
      />
      {/* Remove margin top if banner has previous sibling carousel */}
      <div className='[&_[data-component="CMSColumnsCarousel"]+[data-component="CMSBanner"]]:mt-0 md:[&_[data-component="CMSColumnsCarousel"]+[data-component="CMSBanner"]]:mt-0 lg:[&_[data-component="CMSColumnsCarousel"]+[data-component="CMSBanner"]]:mt-0'>
        <div className="container mb-8 md:mb-12">
          <BlogDetailedHeader blogDetailedHeaderData={data} />
        </div>

        {data.content.map((content, index) =>
          match(content)
            .with({ type: 'columns' } as const, (columnsData) => (
              <ErrorBoundary fallback={<ErrorBoundaryFallback />}>
                <CMSColumns
                  key={index}
                  columnsData={columnsData}
                  // Remove padding for CMSCardVerticalCard in a carousel if it's not inside a banner.
                  // (e.g Ideal for working from home, Suitable for online gaming with no lag)
                  className={cn(
                    '[&[data-component="CMSColumnsStandard"]]:container [&[data-component="CMSColumnsCarousel"]_.CMSCardContentWrapper]:p-0',

                    //Add text color for parent level cms column carousel(icon) - background will always be white
                    // (e.g Ideal for working from home, Suitable for online gaming with no lag)
                    '[&[data-component="CMSColumnsCarousel"]_[data-component="CMSCard"]]:text-black',

                    //CMSColumnsStandard  Add margin bottom if it doesn't have either CMSPlanCardBroadband or TrustPilotWidget
                    //CMSColumnsStandard  (e.g Manage your service in the app section)
                    `[&[data-component="CMSColumnsStandard"]:not(:has([data-component="CMSPlanCardBroadband"],[data-component="TrustPilotWidget"]))]:mb-8 
                   md:[&[data-component="CMSColumnsStandard"]:not(:has([data-component="CMSPlanCardBroadband"],[data-component="TrustPilotWidget"]))]:mb-12 
                   lg:[&[data-component="CMSColumnsStandard"]:not(:has([data-component="CMSPlanCardBroadband"],[data-component="TrustPilotWidget"]))]:mb-20`,

                    //Add text color for h1, h2 and h3
                    `[&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_h1]:text-black 
                 [&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_h2]:text-black 
                 [&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_h3]:text-black`,

                    //Add text deep teal for p tag inside columns
                    '[&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_p]:text-black',

                    // Add margin bottom for p tag
                    '[&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_p]:mb-4',

                    //Set CMSTextAdvanced component to w-full for CMSColumnsStandard
                    '[&[data-component="CMSColumnsStandard"]_[data-component="CMSTextAdvanced"]]:w-full',
                    //Remove margin top if it's first child for ul
                    'cms-render [&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_ul:first-child]:mt-0'
                  )}
                />
              </ErrorBoundary>
            ))
            .with({ type: 'banner' } as const, (bannerData) => (
              <ErrorBoundary fallback={<ErrorBoundaryFallback />}>
                <CMSBanner
                  startingPosition={index}
                  key={index}
                  className={cn(
                    'blog',

                    // Add margin top to h3 tag itself if it is not first child
                    `[&_[data-component="CMSText"]_h2:not(:first-child)]:mt-8 
                   md:[&_[data-component="CMSText"]_h2:not(:first-child)]:mt-12 
                   lg:[&_[data-component="CMSText"]_h2:not(:first-child)]:mt-20`,

                    // Add margin top to h3 tag itself if it is not first child
                    `[&_[data-component="CMSText"]_h3:not(:first-child)]:mt-6 
                   md:[&_[data-component="CMSText"]_h3:not(:first-child)]:mt-8`,

                    // add margin top if it's not the first child
                    '[&:not(:first-child)]:mt-8 md:[&:not(:first-child)]:mt-12 lg:md:[&:not(:first-child)]:mt-20'
                  )}
                  bannerData={bannerData}
                />
              </ErrorBoundary>
            ))
            .with({ type: 'card' } as const, (cardData) => (
              <ErrorBoundary fallback={<ErrorBoundaryFallback />}>
                <CMSCard cardData={cardData} className="container" />
              </ErrorBoundary>
            ))
            .with({ type: 'plan_cards' } as const, (planCardsData) => (
              <ErrorBoundary fallback={<ErrorBoundaryFallback />}>
                <CMSPlanCardsWrapper
                  key={index}
                  planCardsData={planCardsData}
                  className="container"
                />
              </ErrorBoundary>
            ))
            .with({ type: 'tabs' } as const, (tabsData) => (
              <ErrorBoundary fallback={<ErrorBoundaryFallback />}>
                <CMSTabs
                  key={index}
                  tabsData={tabsData}
                  className="container blog"
                />
              </ErrorBoundary>
            ))
            .with({ type: 'text' } as const, (textData) => (
              <ErrorBoundary fallback={<ErrorBoundaryFallback />}>
                <CMSText
                  key={index}
                  textData={textData}
                  className={cn(
                    'container blog',
                    //Add color for h1 top level CMSText
                    '[&_h1]:text-black',
                    //Add color for h2 top level CMSText
                    '[&_h2]:text-black',
                    //Add color for h3 top level CMSText
                    '[&_h3]:text-black',

                    //Add margin bottom for h1 top level CMSText if it has h1
                    '[&:has(h1)]:mb-6 md:[&:has(h1)]:mb-8 lg:[&:has(h1)]:mb-16',

                    //Add margin bottom for h2 top level CMSText if it has h2
                    '[&:has(h2)]:mb-6 md:[&:has(h2)]:mb-8 lg:[&:has(h2)]:mb-16',

                    //Add margin bottom for h3 top level CMSText if it has h3
                    '[&:has(h3)]:mb-6 md:[&:has(h3)]:mb-8 lg:[&:has(h3)]:mb-16',
                    //Add margin top to h2 tag itself
                    '[&_h1]:mt-8 md:[&_h1]:mt-12 lg:[&_h1]:mt-20',

                    //Add margin top to h2 tag itself
                    '[&_h2]:mt-8 md:[&_h2]:mt-12 lg:[&_h2]:mt-20',

                    //Add margin top to h3 tag itself
                    '[&_h3]:mt-6 md:[&_h3]:mt-8',

                    //Add font styles for p tag that has no a tag children top level CMS text
                    '[&_p]:text-black',
                    //Add margin top for p tag that has no a tag children top level CMS text
                    '[&_p]:mt-2 md:[&_p]:mt-4',

                    // Add margin bottom for p tag
                    '[&_p]:mb-4'
                  )}
                />
              </ErrorBoundary>
            ))
            .with({ type: 'text_advanced' } as const, (textAdvancedData) => (
              <ErrorBoundary fallback={<ErrorBoundaryFallback />}>
                <CMSTextAdvanced
                  key={index}
                  textAdvancedData={textAdvancedData}
                  className="container"
                />
              </ErrorBoundary>
            ))
            .with({ type: 'call_to_action' } as const, (data) => (
              <ErrorBoundary fallback={<ErrorBoundaryFallback />}>
                <CMSCTAButton
                  key={index}
                  ctaData={data}
                  // Add container
                  className={cn('container')}
                  // Font size for tablet and desktop breakpoint
                  buttonClassName={cn('md:h-[52px]')}
                />
              </ErrorBoundary>
            ))
            .with({ type: 'image' } as const, (data) => (
              <ErrorBoundary fallback={<ErrorBoundaryFallback />}>
                <CMSImage
                  key={index}
                  className={cn('container', 'my-8 md:my-12')}
                  imageData={data}
                />
              </ErrorBoundary>
            ))
            .with({ type: 'video' } as const, (data) => (
              <ErrorBoundary key={index} fallback={<ErrorBoundaryFallback />}>
                <Suspense fallback={null}>
                  <LazyCMSVideo
                    data={data}
                    className={cn(
                      'container rounded-[15px]',
                      // Add height for video component
                      'h-[163px] md:h-[398px] lg:h-[506px]',
                      // Add margin top and bottom for video component
                      'my-8 md:my-12'
                    )}
                  />
                </Suspense>
              </ErrorBoundary>
            ))
            .with({ type: 'placeholder' } as const, (data) => (
              <ErrorBoundary fallback={<ErrorBoundaryFallback />}>
                <CMSPlaceholder
                  className="container"
                  key={index}
                  placeholderData={data}
                />
              </ErrorBoundary>
            ))
            .otherwise(() => (
              <div key={index} className="container ">
                Unhandled CMSComponent
              </div>
            ))
        )}

        <RelatedArticles slug={data.slug} category={data.categories[0].slug} />
      </div>
    </>
  );
};

export { BlogDetailedPage };
